
import { createApiRequest } from "@/utils/apiUtils";
import { API_URL } from "@/config/api";

export interface TripRating {
  id: string;
  trip: string;
  review: string;
  stars: number;
  created: string;
  modified: string;
}

export interface RatingSummary {
  "1-star": number;
  "2-star": number;
  "3-star": number;
  "4-star": number;
  "5-star": number;
  average_rating: number;
}

export interface RatingsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: TripRating[];
  summary: RatingSummary;
}

export const fetchRatings = async (): Promise<RatingsResponse> => {
  try {
    const response = await fetch(
      `${API_URL}tripratings/`, 
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || "Failed to fetch ratings");
    }
    
    return response.json();
  } catch (error) {
    console.error("Error fetching ratings:", error);
    throw error;
  }
};

export const fetchAllRatings = async (): Promise<RatingsResponse> => {
  let allRatings: TripRating[] = [];
  let url = `${API_URL}tripratings/`;
  let summary: RatingSummary | null = null;
  let count = 0;
  
  try {
    // Fetch first page to get the total count and summary
    const firstResponse = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Token ${localStorage.getItem("authToken")}`,
      },
    });
    
    if (!firstResponse.ok) {
      const errorData = await firstResponse.json();
      throw new Error(errorData.detail || "Failed to fetch ratings");
    }
    
    const firstPageData: RatingsResponse = await firstResponse.json();
    allRatings = [...firstPageData.results];
    summary = firstPageData.summary;
    count = firstPageData.count;
    
    let nextUrl = firstPageData.next;
    
    // Fetch all remaining pages
    while (nextUrl) {
      const response = await fetch(nextUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch ratings page");
      }
      
      const data: RatingsResponse = await response.json();
      allRatings = [...allRatings, ...data.results];
      nextUrl = data.next;
    }
    
    // Return in the same format as a single page response
    return {
      count,
      next: null,
      previous: null,
      results: allRatings,
      summary: summary as RatingSummary,
    };
  } catch (error) {
    console.error("Error fetching all ratings:", error);
    throw error;
  }
};

export interface SubmitReviewPayload {
  trip_id: string;
  ratings: Record<number, number>;
  average_rating: number;
}

export const submitReview = async (payload: SubmitReviewPayload): Promise<any> => {
  try {
    const response = await fetch(
      `${API_URL}tripratings/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Token ${localStorage.getItem("authToken")}`,
        },
        body: JSON.stringify({
          trip: payload.trip_id,
          stars: Math.round(payload.average_rating),
          review: JSON.stringify(payload.ratings),
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.detail || "Failed to submit review");
    }

    return response.json();
  } catch (error) {
    console.error("Error submitting review:", error);
    throw error;
  }
};
